// ============================================================================
// quote-run-open-guard.ts: opening a project during a Quote Run asks first
// ----------------------------------------------------------------------------
// A Quote Run drives one job at a time through the same React state that an
// attended project open or a New Project reset replaces. Either one aborted
// the running job's AI signal and its first photo upload, and the queue
// recorded that job as a Stage 1 storage failure (Harbourline, v6.0.60).
//
// Decision D1 (2026-09-28): while the run is active, the load and reset choke
// points in use-project-persistence.ts refuse with QUOTE_RUN_ACTIVE. They
// never return silently and never abort the job. The refusal shows ONE
// message with two actions:
//   - "Stop the run" stops the run the way the STOP pill does: the queue's
//     stop first, then the job's AI stage is aborted, so the run ends
//     promptly. The job in progress and every job not yet started leave the
//     run list, as on STOP. The operator is told the run is stopping, and a
//     retry before it has stopped says so instead of asking again.
//   - "Keep running" closes the message.
// Opening the job the run is driving (View live, its Run Sheet row) is not a
// change of project: it is shown, never refused.
// Callers that toast their own load errors skip this code, because the
// message has already been shown.
//
// Review round (A3): the action was labelled "Pause the run" and raised only
// the queue event. The event aborts the runner's own controller, but the job's
// current AI stage runs on the project signal, so the stage kept running (and
// spending) for minutes and every retry showed the same question. It was also
// never a pause: there is no resume, and the stopped jobs must be queued again.
// ============================================================================

export const QUOTE_RUN_ACTIVE = 'QUOTE_RUN_ACTIVE' as const;
export const QUOTE_RUN_ACTIVE_TITLE = 'Stop the Quote Run?';
export const QUOTE_RUN_ACTIVE_MESSAGE =
    'A Quote Run is working on its jobs. Opening another project now would stop the job in progress.';
/** What a stop costs, shown under the message. */
export const QUOTE_RUN_STOP_HINT =
    'Stopping the run takes that job and every job not yet started off the run list.';
export const QUOTE_RUN_STOP_LABEL = 'Stop the run';
export const QUOTE_RUN_KEEP_LABEL = 'Keep running';
/** After Stop, and for any open or reset tried before the run has stopped. */
export const QUOTE_RUN_STOPPING_MESSAGE = 'The Quote Run is stopping. Try again once it has stopped.';
/** Stop pressed after the run had already ended on its own. */
export const QUOTE_RUN_ALREADY_STOPPED_MESSAGE = 'The Quote Run has already stopped. Try again now.';
/**
 * New revision and Split during a Quote Run (A3 review round 2). They end in
 * a reload the run refuses, so they are refused before anything reaches the
 * server. The words say what did not happen, not "opening another project".
 */
export const QUOTE_RUN_REVISE_REFUSED_MESSAGE =
    'A Quote Run is working on its jobs, so no new revision was made. Try again once it has stopped.';
export const QUOTE_RUN_SPLIT_REFUSED_MESSAGE =
    'A Quote Run is working on its jobs, so nothing was split. Try again once it has stopped.';
/** The queue stop event. Every stop source raises it; FileExplorer listens. */
export const QUOTE_RUN_STOP_EVENT = 'qm-batch-queue-abort-requested';
export const QUOTE_RUN_STOP_REASON = 'stopped-to-open-a-project';
/**
 * The abort reason when App ends the job's AI stage for a stop raised
 * somewhere other than its own STOP path: the Quote Run drawer's Stop and
 * Clear, the Assistant's queue abort, sign-out.
 */
export const QUOTE_RUN_STOP_RAISED_ELSEWHERE_REASON = 'quote-run-stopped';

export interface QuoteRunStopListenerDeps {
    /** App's batchActiveRef, read when the event arrives. */
    isRunActive: () => boolean;
    /** Marks the run as stopping, so a retried open says so. */
    markStopRequested: () => void;
    /** App's STOP path (cancelAiPipeline): raises the queue stop, then aborts the job's AI stage. */
    stopAiStage: (options: { reason: string; serviceStopOwnedByCaller: true }) => void;
}

/**
 * App's listener for the queue stop event (A3 review round 4). Every stop
 * source raises that event. The drawer's Stop and Clear, the Assistant and
 * sign-out raised only the event, which aborts the runner's own controller,
 * but the job's current AI stage runs on the project signal and the runner
 * awaits it with no abort race. So the stage ran on (and spent) for minutes,
 * and every open in that time said the run was stopping.
 *
 * During a run the listener marks the run as stopping and ends the job's AI
 * stage through App's STOP path, the same as the STOP pill and the run bar.
 * That path raises the event itself with `detail.aiStageStopped: true`; the
 * listener only marks that one, so the stage is aborted once and the listener
 * never calls itself. The stop it starts says `serviceStopOwnedByCaller`,
 * because the original event's raiser, or the File Explorer listener for that
 * event, already settles the queue stop. Outside a run it does nothing: the
 * work on screen is not the run's.
 */
export function createQuoteRunStopListener(deps: QuoteRunStopListenerDeps): (event: Event) => void {
    return (event: Event) => {
        if (!deps.isRunActive()) return;
        deps.markStopRequested();
        const detail = (event as CustomEvent | null | undefined)?.detail;
        if (detail && typeof detail === 'object' && (detail as { aiStageStopped?: unknown }).aiStageStopped === true) return;
        deps.stopAiStage({ reason: QUOTE_RUN_STOP_RAISED_ELSEWHERE_REASON, serviceStopOwnedByCaller: true });
    };
}

/** What the operator tried to do: open a project, or reset to a new one. */
export type QuoteRunRefusedChange = 'open' | 'reset';

export interface QuoteRunActiveError extends Error {
    code: typeof QUOTE_RUN_ACTIVE;
    change: QuoteRunRefusedChange;
    projectId: string | null;
    retryable: true;
}

export interface QuoteRunActiveConfirmOptions {
    title: string;
    message: string;
    hint: string;
    confirmText: string;
    cancelText: string;
    confirmColor: 'orange';
    icon: 'warning';
    defaultFocus: 'cancel';
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Returned by the checked project open when the project asked for is the job
 * the Quote Run is driving. Nothing was loaded: QuoteMaker is showing the job.
 * A caller that moves to a stage after an open must not move this one.
 */
export const QUOTE_RUN_JOB_SHOWN: Readonly<{ quoteRunJobShown: true }> = Object.freeze({ quoteRunJobShown: true as const });

export function isQuoteRunJobShown(value: unknown): boolean {
    return value === QUOTE_RUN_JOB_SHOWN;
}

/**
 * The refusal. Its message is the one the operator sees, so a caller that
 * surfaces `error.message` (an Assistant tool result, say) says the same
 * thing. It is deliberately NOT an AbortError: nothing was cancelled, and the
 * abort classifiers must not read it as a stop.
 */
export function createQuoteRunActiveError(
    change: QuoteRunRefusedChange,
    projectId?: unknown,
): QuoteRunActiveError {
    const error = new Error(QUOTE_RUN_ACTIVE_MESSAGE) as QuoteRunActiveError;
    error.name = 'QuoteRunActiveError';
    error.code = QUOTE_RUN_ACTIVE;
    error.change = change;
    error.projectId = projectId == null || projectId === '' ? null : String(projectId);
    error.retryable = true;
    return error;
}

export function isQuoteRunActiveError(error: unknown): error is QuoteRunActiveError {
    return !!error
        && typeof error === 'object'
        && (error as { code?: unknown }).code === QUOTE_RUN_ACTIVE;
}

/** For `.catch()` on a fire-and-forget open: swallow the refusal, rethrow the rest. */
export function ignoreQuoteRunActive(error: unknown): void {
    if (!isQuoteRunActiveError(error)) throw error;
}

/**
 * The one message, in the shape App's showConfirm (ConfirmModal) takes.
 * `onStop` is the caller's stop: App's STOP path, which raises the queue stop
 * and then aborts the job's AI stage. These options raise nothing themselves.
 */
export function quoteRunActiveConfirmOptions(onStop: () => void): QuoteRunActiveConfirmOptions {
    return {
        title: QUOTE_RUN_ACTIVE_TITLE,
        message: QUOTE_RUN_ACTIVE_MESSAGE,
        hint: QUOTE_RUN_STOP_HINT,
        confirmText: QUOTE_RUN_STOP_LABEL,
        cancelText: QUOTE_RUN_KEEP_LABEL,
        confirmColor: 'orange',
        icon: 'warning',
        // Keep running is the safe key: a reflexive Enter must not stop a run.
        defaultFocus: 'cancel',
        onConfirm: () => { onStop(); },
        onCancel: () => {},
    };
}

/**
 * Show the message one task later. ConfirmModal runs a button's handler and
 * then closes itself, so a refusal raised inside another dialog's button
 * (New Project's "Start New", the unsaved-changes "Save & open") would be
 * closed in the same click if it opened synchronously.
 */
export function showQuoteRunActiveNotice(
    showConfirm: ((options: QuoteRunActiveConfirmOptions) => void) | null | undefined,
    onStop: () => void,
): void {
    if (typeof showConfirm !== 'function') return;
    setTimeout(() => { showConfirm(quoteRunActiveConfirmOptions(onStop)); }, 0);
}
